import { motion } from "framer-motion";
import { Wallet, Coins, CheckCircle } from "@phosphor-icons/react";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    n: "01",
    title: "Connect your wallet",
    body: "Link the wallets you already use. No new accounts, no seed-phrase juggling — self-custody stays with you.",
    Icon: Wallet,
  },
  {
    n: "02",
    title: "Enter the amount",
    body: "Type how much you want to move. We handle the network, the routing and the gas so you don't have to.",
    Icon: Coins,
  },
  {
    n: "03",
    title: "Confirm the transfer",
    body: "Authorize directly from your wallet. Funds land in seconds — no addresses, no copy-paste, no mistakes.",
    Icon: CheckCircle,
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative bg-[#f6f6f7] py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid md:grid-cols-[0.9fr_1.4fr] gap-14 lg:gap-24">
        <div className="md:sticky md:top-32 h-fit">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            <span className="h-1.5 w-1.5 rounded-full bg-[#f5d949]" />
            How it works
          </p>
          <h2 className="mt-5 font-display font-black tracking-[-0.02em] leading-[0.95] text-4xl md:text-6xl">
            Deposit funds in seconds.
          </h2>
          <p className="mt-6 text-lg text-zinc-600 max-w-sm">
            Three taps between you and a completed transfer.
          </p>
          <div className="mt-8 flex flex-col gap-2 text-zinc-500 font-mono text-sm">
            <span>— No long wallet addresses</span>
            <span>— No copy and paste</span>
            <span>— No mistakes</span>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`step-${s.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
              className="group relative rounded-3xl border border-black/10 bg-white p-8 md:p-10 hover:border-black/25 transition-colors"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <span className="font-mono text-sm text-[#f5d949]">{s.n}</span>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-zinc-600 max-w-md leading-relaxed">
                    {s.body}
                  </p>
                </div>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#f5d949] transition-colors">
                  <s.Icon
                    size={26}
                    weight="bold"
                    className="text-black"
                  />
                </div>
              </div>
            </motion.div>
          ))}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-2 font-display text-2xl font-bold text-[#0a0a0a] pl-2"
          >
            That&apos;s it.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
